import { useEffect, useState, useCallback } from "react";
import { getAdminHubPartners } from "../../services/ingroove/adminService";
import { formatDate } from "../../utils/formatters";
import Avatar from "../../components/common/Avatar";
import Badge from "../../components/common/Badge";
import PageHeader from "../../components/common/PageHeader";
import { Search, ChevronLeft, ChevronRight, Handshake } from "lucide-react";

const roleBadgeVariant = (role) => {
  switch (role?.toLowerCase()) {
    case "admin":
      return "danger";
    case "operations":
      return "info";
    case "finance":
      return "warning";
    case "content":
      return "success";
    default:
      return "default";
  }
};

const HubPartners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ totalRecords: 0, totalPages: 0 });

  const fetchPartners = useCallback(async (searchVal, pageVal) => {
    setLoading(true);
    try {
      const res = await getAdminHubPartners({
        search: searchVal,
        pageNumber: pageVal,
        pageSize: 20,
      });
      setPartners(res.data);
      setMeta({ totalRecords: res.totalRecords, totalPages: res.totalPages });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1);
      fetchPartners(search, 1);
    }, 400);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    fetchPartners(search, page);
  }, [page]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Hub Partners"
        subtitle={`${meta.totalRecords.toLocaleString()} partners across all hubs`}
      />

      {/* Search */}
      <div className="relative max-w-sm">
        <Search
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
        />
        <input
          type="text"
          placeholder="Search by name, email or hub..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2 text-sm bg-bg-surface border border-border rounded-lg text-text-primary placeholder-text-hint focus:outline-none focus:border-primary transition"
        />
      </div>

      {/* Table */}
      <div className="bg-bg-surface border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-bg-elevated text-text-muted text-xs uppercase">
            <tr>
              <th className="px-5 py-3 text-left">Partner</th>
              <th className="px-5 py-3 text-left">Hub</th>
              <th className="px-5 py-3 text-left">Role</th>
              <th className="px-5 py-3 text-left">Status</th>
              <th className="px-5 py-3 text-left">Joined</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="animate-pulse">
                  <td className="px-5 py-3">
                    <div className="h-4 bg-bg-elevated rounded w-36" />
                  </td>
                  <td className="px-5 py-3">
                    <div className="h-4 bg-bg-elevated rounded w-28" />
                  </td>
                  <td className="px-5 py-3">
                    <div className="h-4 bg-bg-elevated rounded w-20" />
                  </td>
                  <td className="px-5 py-3">
                    <div className="h-4 bg-bg-elevated rounded w-16" />
                  </td>
                  <td className="px-5 py-3">
                    <div className="h-4 bg-bg-elevated rounded w-24" />
                  </td>
                </tr>
              ))
            ) : partners.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-5 py-12 text-center">
                  <Handshake
                    size={24}
                    className="text-border-hover mx-auto mb-2"
                  />
                  <p className="text-xs text-text-muted">
                    No hub partners found
                  </p>
                </td>
              </tr>
            ) : (
              partners.map((partner) => (
                <tr
                  key={partner.id}
                  className="hover:bg-bg-elevated transition"
                >
                  {/* Partner */}
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar
                        name={partner.name}
                        surname={partner.surname}
                        image={partner.image}
                        size="sm"
                      />
                      <div>
                        <p className="text-text-primary font-medium">
                          {partner.name} {partner.surname}
                        </p>
                        <p className="text-xs text-text-muted">
                          {partner.email}
                        </p>
                      </div>
                    </div>
                  </td>

                  {/* Hub */}
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      {partner.hubImage ? (
                        <img
                          src={partner.hubImage}
                          alt={partner.hubName}
                          className="w-6 h-6 rounded-md object-cover"
                        />
                      ) : (
                        <div className="w-6 h-6 rounded-md bg-bg-elevated flex items-center justify-center">
                          <Handshake size={12} className="text-text-muted" />
                        </div>
                      )}
                      <span className="text-text-secondary text-sm">
                        {partner.hubName}
                      </span>
                    </div>
                  </td>

                  {/* Role */}
                  <td className="px-5 py-3">
                    <Badge
                      label={partner.role}
                      variant={roleBadgeVariant(partner.role)}
                    />
                  </td>

                  {/* Status */}
                  <td className="px-5 py-3">
                    <Badge
                      label={partner.isActive ? "Active" : "Pending"}
                      variant={partner.isActive ? "success" : "warning"}
                    />
                  </td>

                  {/* Joined */}
                  <td className="px-5 py-3 text-text-muted text-xs">
                    {formatDate(partner.joinedAt)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {meta.totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-text-muted">
          <span>
            Page {page} of {meta.totalPages} —{" "}
            {meta.totalRecords.toLocaleString()} partners
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-lg border border-border hover:border-primary hover:text-primary transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
              disabled={page === meta.totalPages}
              className="p-1.5 rounded-lg border border-border hover:border-primary hover:text-primary transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HubPartners;
